import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtDMY } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Megaphone, Sparkles, UserCheck } from "lucide-react";
import { HolidayVolunteerDialog, HolidayRequestStatus } from "@/components/HolidayVolunteer";

export function UpcomingHolidays({ date }) {
  const { t } = useTranslation();
  const [ask, setAsk] = useState(null);
  const { data: items = [] } = useQuery({
    queryKey: ["holidays-upcoming", date],
    queryFn: async () => (await api.get(`/holidays/upcoming?date=${date}&days=14`)).data,
    refetchInterval: 60000,
  });
  if (items.length === 0) return null;
  return (
    <>
      {items.map((h) => {
        const assigned = h.on;
        const staffed = h.filled || (h.rule === "rostered" && h.on.length > 0);
        return (
          <div key={h.date} className={`flex flex-wrap items-center gap-2 rounded-md border px-4 py-2 text-sm ${staffed ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid={`upcoming-holiday-${h.date}`}>
            {staffed ? <UserCheck className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
            <span className="font-semibold">
              {t("Public holiday in {{count}} day(s)", { count: h.days_away })}: {h.holiday} · {fmtDMY(h.date)}.
            </span>
            {staffed ? (
              <span data-testid={`upcoming-holiday-staffed-${h.date}`}>
                {t("Staff assigned — {{count}} cleaner(s) rostered on", { count: assigned.length })}: <b>{assigned.map((c) => c.name).join(", ") || "—"}</b>.
                {h.request?.needed ? ` ${t("Required {{n}} — all positions filled.", { n: h.request.needed })}` : ""}
              </span>
            ) : h.rule === "rostered" ? (
              <span>{t("Normal roster applies — anyone who works is marked \"Public holiday worked\" on their timesheet.")}</span>
            ) : (
              <span>
                {h.on.length > 0
                  ? t("{{count}} cleaner(s) agreed so far", { count: h.on.length }) + `: ${h.on.map((c) => c.name).join(", ")}. `
                  : t("Everyone is off by default. ")}
                {h.request?.status === "open" ? t("Still waiting on replies.") : t("Offer the work out now so it's sorted in time.")}
              </span>
            )}
            {h.rule !== "rostered" && !staffed && h.request?.status !== "open" && (
              <Button size="sm" variant="outline" className="ml-auto h-7 gap-1 border-amber-400 bg-white text-xs" onClick={() => setAsk(h)} data-testid={`ask-volunteers-${h.date}`}>
                <Megaphone className="h-3.5 w-3.5" /> {t("Ask who can work")}
              </Button>
            )}
            {h.request && <HolidayRequestStatus date={h.date} />}
          </div>
        );
      })}
      {ask && (
        <HolidayVolunteerDialog open onClose={() => setAsk(null)} date={ask.date} holiday={ask.holiday}
          cleaners={ask.off.filter((c) => c.why === "Public holiday" || c.why === "Public holiday — rostered day off")} />
      )}
    </>
  );
}
